import React from 'react';
import {
    FileUp,
    CheckCircle,
    XCircle,
    FileText,
    Plus,
    Download,
    Trash2,
    X
} from 'lucide-react';

import { useToast } from './toastContext';
import {
    API_URL,
    formatFileSize
} from './houseHelpers';

const ActItem = ({
    act,
    canChangeHouses,
    onDownload,
    onDelete
}) => {
    return (
        <div className="bg-slate-900/50 rounded-lg p-3 flex items-center gap-3 hover:bg-slate-900 transition">
            <FileText className="w-8 h-8 text-orange-400 flex-shrink-0" />

            <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-sm truncate">
                    {act.fileName}
                </div>

                <div className="text-slate-500 text-xs mt-1">
                    {formatFileSize(act.fileSize)} • Загружен: {act.uploadDate}
                    {act.uploadedBy && ` • ${act.uploadedBy}`}
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => onDownload(act.id, act.fileName)}
                    className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                    title="Скачать"
                >
                    <Download className="w-4 h-4" />
                </button>

                {canChangeHouses && (
                    <button
                        onClick={() => onDelete(act.id)}
                        className="p-1.5 bg-red-600 hover:bg-red-500 text-white rounded transition"
                        title="Удалить"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

const ViewActsModal = ({
    nodeId,
    acts,
    canChangeHouses,
    onClose,
    onUploadAct,
    onDeleteAct
}) => {
    const toast = useToast();

    const handleDownload = async (actId, fileName) => {
        try {
            const token = localStorage.getItem('token');

            const response = await fetch(
                `${API_URL}/api/houses/nodes/acts/${actId}/download`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Ошибка ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');

            link.href = url;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('❌ Ошибка скачивания акта:', err);
            toast.error(`Не удалось скачать акт: ${err.message}`);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <FileUp className="w-5 h-5 text-orange-400" />
                        Документы узла учёта
                    </h3>

                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-6">
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-400" />
                                Акты ввода в эксплуатацию
                            </h4>

                            {canChangeHouses && (
                                <button
                                    onClick={() => onUploadAct('commissioning')}
                                    className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                                >
                                    <Plus className="w-3 h-3" />
                                    Загрузить
                                </button>
                            )}
                        </div>

                        {acts.commissioningActs.length === 0 ? (
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center text-slate-500 text-sm">
                                Нет актов ввода в эксплуатацию
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {acts.commissioningActs.map((act) => (
                                    <ActItem
                                        key={act.id}
                                        act={act}
                                        canChangeHouses={canChangeHouses}
                                        onDownload={handleDownload}
                                        onDelete={onDeleteAct}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-400" />
                                Акты вывода из эксплуатации
                            </h4>

                            {canChangeHouses && (
                                <button
                                    onClick={() => onUploadAct('decommissioning')}
                                    className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                                >
                                    <Plus className="w-3 h-3" />
                                    Загрузить
                                </button>
                            )}
                        </div>

                        {acts.decommissioningActs.length === 0 ? (
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center text-slate-500 text-sm">
                                Нет актов вывода из эксплуатации
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {acts.decommissioningActs.map((act) => (
                                    <ActItem
                                        key={act.id}
                                        act={act}
                                        canChangeHouses={canChangeHouses}
                                        onDownload={handleDownload}
                                        onDelete={onDeleteAct}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <FileText className="w-4 h-4 text-blue-400" />
                                Другие документы
                            </h4>

                            {canChangeHouses && (
                                <button
                                    onClick={() => onUploadAct('other')}
                                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                                >
                                    <Plus className="w-3 h-3" />
                                    Загрузить
                                </button>
                            )}
                        </div>

                        {!acts.otherActs || acts.otherActs.length === 0 ? (
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center text-slate-500 text-sm">
                                Нет других документов
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {acts.otherActs.map((act) => (
                                    <ActItem
                                        key={act.id}
                                        act={act}
                                        canChangeHouses={canChangeHouses}
                                        onDownload={handleDownload}
                                        onDelete={onDeleteAct}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end sticky bottom-0 bg-slate-800">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                    >
                        Закрыть
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewActsModal;